import React, { useState, useEffect } from 'react';
import Tree from 'react-d3-tree';
import '../styles/Binary.css';
import Button from "../../shared/Button";

class TreeNode {
  constructor(value) {
    this.value = value;
    this.left = null;
    this.right = null;
  }
}

export default function BinaryTreeVisualizer() {
  const [arrayInput, setArrayInput] = useState('');
  const [steps, setSteps] = useState([]);
  const [comparisonLogs, setComparisonLogs] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [traversalOrder, setTraversalOrder] = useState([]);
  const [traversalType, setTraversalType] = useState('');
  const [highlightedNodes, setHighlightedNodes] = useState([]);

  const parseArrayInput = (inputStr) => {
    const tokens = inputStr.trim().split(/\s+/);
    const array = tokens.map((elem) => parseInt(elem, 10));
    return array.every((num) => !isNaN(num)) ? array : null;
  };

  const insertNode = (node, value, stepLogs) => {
    if (!node) {
      stepLogs.push(`Inserting ${value}`);
      return new TreeNode(value);
    }

    if (value < node.value) {
      stepLogs.push(`${value} < ${node.value}, going left`);
      node.left = insertNode(node.left, value, stepLogs);
    } else if (value > node.value) {
      stepLogs.push(`${value} > ${node.value}, going right`);
      node.right = insertNode(node.right, value, stepLogs);
    } else {
      stepLogs.push(`${value} already exists, skipping`);
    }

    return node;
  };

  const buildBST = () => {
    const array = parseArrayInput(arrayInput);
    if (!array) {
      alert('Invalid input! Please enter numbers separated by spaces.');
      return;
    }

    let root = null;
    const stepData = [];
    let allLogs = [];

    array.forEach(value => {
      const stepLogs = [];
      root = insertNode(root, value, stepLogs);
      allLogs = [...allLogs, ...stepLogs];
      stepData.push({
        tree: formatTreeData(root),
        logs: [...allLogs]
      });
    });

    setSteps(stepData);
    setComparisonLogs(allLogs);
    setCurrentStep(0);
  };

  const formatTreeData = (node) => {
    if (!node) return null;
    return {
      name: node.value.toString(),
      children: [
        formatTreeData(node.left),
        formatTreeData(node.right),
      ].filter(child => child !== null),
    };
  };

  const traverseTree = async (type) => {
    setTraversalType(type);
    setTraversalOrder([]);
    setHighlightedNodes([]);

    const traverse = async (node) => {
      if (!node) return;

      const visit = async () => {
        setTraversalOrder((prev) => [...prev, node.name]);
        setHighlightedNodes((prev) => [...prev, node.name]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      };

      if (type === 'preorder') await visit();
      if (node.children && node.children[0]) await traverse(node.children[0]);
      if (type === 'inorder') await visit();
      if (node.children && node.children[1]) await traverse(node.children[1]);
      if (type === 'postorder') await visit();
    };

    if (steps[currentStep] && steps[currentStep].tree) {
      await traverse(steps[currentStep].tree);
    }
  };

  return (
    <div className="tree-traversal-visualizer">
      <div className="Bsidebar">
        <h2>Binary Search Tree Visualizer</h2>
        <input
          className="Barray-input"
          value={arrayInput}
          onChange={(e) => setArrayInput(e.target.value)}
          placeholder="Enter numbers separated by spaces..."
        />
        <Button onClick={buildBST}>Build BST</Button>
        <div className="traversal-buttons">
          <Button variant="" size="sm" onClick={() => traverseTree('preorder')} disabled={!steps.length}>Preorder</Button>
          <Button variant="" size="sm" onClick={() => traverseTree('inorder')} disabled={!steps.length}>Inorder</Button>
          <Button variant="" size="sm" onClick={() => traverseTree('postorder')} disabled={!steps.length}>Postorder</Button>
        </div>
        {traversalOrder.length > 0 && (
          <div className="traversal-order">
            <h3>{traversalType.charAt(0).toUpperCase() + traversalType.slice(1)} Traversal Order:</h3>
            <p>{traversalOrder.join(' > ')}</p>
          </div>
        )}
        {comparisonLogs.length > 0 && (
          <div className="comparison-logs">
            <h3>Comparison Logs:</h3>
            <ul>
              {comparisonLogs.map((log, index) => (
                <li 
                  key={index} 
                  className={index < steps[currentStep]?.logs.length ? 'highlighted' : ''}
                >
                  {log}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="Bmain-content">
        {steps.length > 0 && (
          <div className="step-trees">
            <h3>Step-by-Step Tree Construction:</h3>
            <div className="step-controls">
              <Button variant="secondary" size="sm" onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}>Previous</Button>
              <span>Step {currentStep + 1} of {steps.length}</span>
              <Button variant="secondary" size="sm" onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))} disabled={currentStep === steps.length - 1}>Next</Button>
            </div>
            <div className="tree-container">
              <Tree
                data={steps[currentStep].tree}
                orientation="vertical"
                pathFunc="step"
                translate={{ x: 200, y: 50 }}
                separation={{ siblings: 1, nonSiblings: 2 }}
                nodeSvgShape={{ shape: 'circle', shapeProps: { r: 15 } }}
                renderCustomNodeElement={(rd3tProps) => (
                  <g>
                    <circle
                      r={26}
                      fill={highlightedNodes.includes(rd3tProps.nodeDatum.name) ? '#22c55e' : '#1e3a5f'}
                      stroke={highlightedNodes.includes(rd3tProps.nodeDatum.name) ? '#86efac' : '#60a5fa'}
                      strokeWidth={2}
                    />
                    <text
                      fill="rgba(255,255,255,0.9)"
                      strokeWidth="0"
                      x={0}
                      y={5}
                      fontSize={12}
                      fontWeight="600"
                      textAnchor="middle"
                    >
                      {rd3tProps.nodeDatum.name}
                    </text>
                  </g>
                )}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}